<template>
    <dialog v-if="isRendered" ref="dialog" class="e-action-sheet" :dir="$store.state.isRtl ? `rtl` : `ltr`" :class="{ 'is-closing': isClosing, 'transparent': !noTransparency }"
        data-test="action-sheet" @close="onDialogClose" @cancel="onDialogCancel" @click="onBackdropClick">
        <div class="panel">
            <div class="action-header mb-3" :class="{ 'btn btn-sheet not-interactive': isMobileLayout }" v-if="!noHeader && (title || description)">
                <slot name="header">
                    <h4 v-if="title" class="title mt-0">{{title|tr}}</h4>
                    <div v-if="description" class="description">{{description|tr}}</div>
                </slot>
            </div>
            <div class="sheets" @click="hide">
                <slot></slot>
            </div>
            <div class="action-footer mt-3" v-if="!noCancel">
                <slot name="actions">
                    <e-action-sheet-item variant="gray" @click="hide">
                        <span v-tr>Cancel|Annuler</span>
                    </e-action-sheet-item>
                </slot>
            </div>
        </div>
    </dialog>
</template>

<script>
import EActionSheetItem from "@vue-components/components/e-action-sheet-item.vue";

export default {
    name: `e-action-sheet`,
    components: {EActionSheetItem},
    props: {
        value: { type: Boolean },
        title: { type: String },
        description: { type: String },
        noHeader: { type: Boolean },
        noTransparency: { type: Boolean },
        noCancel: { type: Boolean }
    },
    computed: {
        isRendered() {
            return this.value || this.isClosing;
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.cancelCloseAnimation();
                    this.$nextTick(() => this.openDialog());
                } else {
                    this.closeDialog();
                }
            }
        }
    },
    data() {
        return {
            isClosing: false,
            closeTimer: null
        };
    },
    mounted() {
        if (this.value) {
            this.$nextTick(() => this.openDialog());
        }
    },
    beforeDestroy() {
        this.clearCloseTimer();
        this.removeDialogFromBody();
    },
    beforeUnmount() {
        this.clearCloseTimer();
        this.removeDialogFromBody();
    },
    methods: {
        show() {
            this.$emit(`input`, true);
        },
        hide() {
            this.$emit(`input`, false);
        },
        openDialog() {
            this.cancelCloseAnimation();
            this.appendDialogToBody();
            if (this.$refs.dialog.open) return;
            this.$refs.dialog.showModal();
        },
        closeDialog() {
            const dialog = this.$refs.dialog;
            if (!dialog || !dialog.open || this.isClosing) return;

            this.isClosing = true;
            this.clearCloseTimer();
            this.closeTimer = setTimeout(() => {
                this.finishCloseDialog();
            }, 180);
        },
        finishCloseDialog() {
            this.clearCloseTimer();

            if (this.$refs.dialog.open) {
                this.$refs.dialog.close();
            }

            this.isClosing = false;
        },
        cancelCloseAnimation() {
            this.clearCloseTimer();
            this.isClosing = false;
        },
        clearCloseTimer() {
            if (!this.closeTimer) return;
            clearTimeout(this.closeTimer);
            this.closeTimer = null;
        },
        appendDialogToBody() {
            if (this.$refs.dialog.parentNode === document.body) return;
            document.body.appendChild(this.$refs.dialog);
        },
        removeDialogFromBody() {
            const dialog = this.$refs.dialog;
            if (!dialog || dialog.parentNode !== document.body) return;
            document.body.removeChild(dialog);
        },
        onDialogClose() {
            this.cancelCloseAnimation();
            if (this.value) {
                this.$emit(`input`, false);
            }
        },
        onDialogCancel(event) {
            event.preventDefault();
            this.hide();
        },
        onBackdropClick(event) {
            if (event.target === this.$refs.dialog) {
                this.hide();
            }
        }
    }
}
</script>

<style lang="scss">
.e-action-sheet {
    --e-action-sheet-enter-y: 18px;
    --e-action-sheet-enter-duration: 240ms;
    --e-action-sheet-exit-duration: 180ms;
    --e-action-sheet-enter-ease: cubic-bezier(0.22, 1, 0.36, 1);
    --e-action-sheet-exit-ease: cubic-bezier(0.4, 0, 1, 1);
    --e-action-sheet-backdrop-end: 0.6;

    border: none;
    padding: 0;
    margin: auto;
    inset: 0;
    background: transparent;
    color: inherit;
    overflow: visible;
    position: fixed;
    width: min(500px, calc(100% - 32px));
    height: fit-content;
    max-width: none;
    max-height: calc(100dvh - 32px);
}

.e-action-sheet::backdrop {
    background: rgba(0, 0, 0, var(--e-action-sheet-backdrop-end));
    opacity: 0;
    animation: e-action-sheet-backdrop-in 180ms ease-out both;
}

.e-action-sheet.is-closing::backdrop {
    animation: e-action-sheet-backdrop-out 140ms linear both;
}

.e-action-sheet .panel {
    display: flex;
    flex-direction: column;
    padding: 20px 15px;
    background: #e0e0e0;
    border-radius: 20px;
    overflow: visible;
    transform-origin: center center;
    will-change: transform, opacity;
    animation: e-action-sheet-panel-in var(--e-action-sheet-enter-duration) var(--e-action-sheet-enter-ease) both;
}

.e-action-sheet.is-closing .panel {
    animation: e-action-sheet-panel-out var(--e-action-sheet-exit-duration) var(--e-action-sheet-exit-ease) both;
}

.e-action-sheet {
    .sheets {
        max-height: calc(100dvh - 100px);
        overflow: auto;
        border-radius: 15px;

        .btn.btn-sheet {
            text-align: start;
            border-radius: 0;

            &:disabled {
                opacity: 1 !important;
                color: rgba(black, 0.6);
            }

            &:first-child {
                border-start-start-radius: 15px;
                border-start-end-radius: 15px;
            }

            &:last-child {
                border-end-start-radius: 15px;
                border-end-end-radius: 15px;
            }
        }

        hr {
            margin-block: 0.5rem;
            margin-inline: 0;
        }
    }

    .action-header {
        text-align: center;

        .description {
            opacity: 0.75;
            font-size: 10pt;
        }
    }

    @media(max-width: 576px) {
        --e-action-sheet-enter-y: 100%;
        --e-action-sheet-enter-duration: 280ms;
        --e-action-sheet-enter-ease: cubic-bezier(0.32, 0.72, 0, 1);
        inset: auto;
        inset-inline: 0;
        inset-block-end: max(32px, env(safe-area-inset-bottom));
        width: calc(100% - 32px);
        max-height: 100dvh;

        .panel {
            padding: 0;
            background: #e0e0e0;
            border-radius: 0;
            transform-origin: center bottom;
        }
    }

    &.transparent {
        @media(max-width: 576px) {
            .panel {
                background: none;
            }
        }
    }

    @media (prefers-reduced-motion: reduce) {
        --e-action-sheet-enter-y: 0px;
        --e-action-sheet-enter-duration: 140ms;
        --e-action-sheet-exit-duration: 120ms;

        .panel {
            will-change: opacity;
            animation-name: e-action-sheet-panel-fade-in;
        }

        &.is-closing .panel {
            animation-name: e-action-sheet-panel-fade-out;
        }

        &::backdrop {
            animation-duration: 120ms;
        }

        &.is-closing::backdrop {
            animation-duration: 100ms;
        }
    }
}

@keyframes e-action-sheet-panel-in {
    0% {
        opacity: 0;
        transform: translateY(var(--e-action-sheet-enter-y)) scale(0.98);
    }

    100% {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes e-action-sheet-panel-out {
    0% {
        opacity: 1;
        transform: translateY(0) scale(1);
    }

    100% {
        opacity: 0;
        transform: translateY(calc(var(--e-action-sheet-enter-y) * 0.72)) scale(0.99);
    }
}

@keyframes e-action-sheet-backdrop-in {
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
}

@keyframes e-action-sheet-backdrop-out {
    0% {
        opacity: 1;
    }

    100% {
        opacity: 0;
    }
}

@keyframes e-action-sheet-panel-fade-in {
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
}

@keyframes e-action-sheet-panel-fade-out {
    0% {
        opacity: 1;
    }

    100% {
        opacity: 0;
    }
}
</style>
